/**
 * The page a sign-in returns to (ENG-499).
 *
 * RequireAuth and fetchWrapper store the page a signed-out visitor asked for
 * under POST_LOGIN_REDIRECT_KEY, and OAuthPage navigates there after the
 * login. Anything that can write sessionStorage can write that value, and
 * `navigate('//evil.com')` leaves the site, so the value is only followed
 * when it is a path on this origin.
 */

export const POST_LOGIN_REDIRECT_KEY = 'post-login-redirect';

// Any origin will do: the path is resolved against it only to see whether it
// stays there.
const PROBE_ORIGIN = 'https://songshake.invalid';

/**
 * `path` when it is a same-origin relative path, otherwise null.
 *
 * It must start with a single `/`. A second `/` or a `\` after it makes a
 * protocol-relative URL (browsers read `/\evil.com` as `//evil.com`), and a
 * scheme (`https:`, `javascript:`) can't start with `/` at all. Control
 * characters are refused outright, because the URL parser strips tabs and
 * newlines and `/\t/evil.com` would otherwise become `//evil.com`. As a last
 * check the path is resolved, and it must not leave the origin.
 * @param {unknown} path
 * @returns {string|null}
 */
export function sanitizeReturnPath(path) {
  if (typeof path !== 'string' || path.length === 0) return null;
  if (path[0] !== '/') return null;
  if (path[1] === '/' || path[1] === '\\') return null;
  if (path.includes('\\')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(path)) return null;
  try {
    if (new URL(path, PROBE_ORIGIN).origin !== PROBE_ORIGIN) return null;
  } catch {
    return null;
  }
  return path;
}

/** Whether a session is stored. */
export const isSignedIn = () => Boolean(sessionStorage.getItem('jwt'));

/**
 * Save `path` (the current page by default) as the page to come back to
 * after signing in, if it is a same-origin path (ENG-503). Callers then send
 * the visitor to /login.
 * @param {string} [path]
 */
export function rememberReturnPath(path = window.location.pathname + window.location.search + window.location.hash) {
  const returnPath = sanitizeReturnPath(path);
  if (returnPath) sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, returnPath);
}
